import {
  AcceptConsentResponse,
  AcceptLoginRequest,
  AcceptLoginResponse,
  AcceptLogoutResponse,
  HydraShell,
  InitialConsentResponse,
  InitialLoginResponse,
  InitialLogoutResponse,
} from '@weave/schema-gen-ts/dist/schemas/hydra-shell/hydra_shell.pb';
import { navigateToUrl } from '@frontend/auth';
import { getDecodedWeaveToken, localStorageHelper } from '@frontend/auth-helpers';
import { baseFetch, authorizedFetch } from './fetch-wrapper';

const logoutChallenge = 'logout_challenge';
const loginChallenge = 'login_challenge';
const consentChallenge = 'consent_challenge';

class HydraShellService {
  private challenge: string | undefined;
  private cancel: string | undefined;

  // getCurrentState will attempt to identify if we are starting a new login flow, or if we are in the middle of a login flow
  public getCurrentState(): string {
    const consentChallenge = this.getConsentChallenge();
    const loginChallenge = this.getLoginChallenge();
    const logoutChallenge = this.getLogoutChallenge();
    if (!!consentChallenge) {
      return 'consent';
    }
    if (!!loginChallenge) {
      return 'login';
    }
    if (!!logoutChallenge) {
      return 'logout';
    }
    return 'none';
  }

  public async initializeOAuth2Flow(): Promise<InitialLoginResponse> {
    this.challenge = this.getLoginChallenge();
    const res = await HydraShell.InitialOAuth2LoginRequest(baseFetch<InitialLoginResponse>(), {
      challenge: this.challenge,
    });
    if (!!res.subject) {
      this.cancel = res.subject;
    }
    return res;
  }

  public async acceptOAuth2Flow(weaveJWT: string): Promise<AcceptLoginResponse> {
    const decodedWeaveToken = getDecodedWeaveToken();
    const challenge = this.challenge || this.getLoginChallenge();
    if (!challenge) {
      throw new Error('No challenge found');
    }
    // success logging in the token is stored in local storage
    const request: AcceptLoginRequest = {
      challenge: this.challenge || this.getLoginChallenge(),
      subject: decodedWeaveToken?.username,
      rememberFor: 4 * 60 * 60, // When the session expires, in seconds. Set this to 0 so it will never expire. Backend may or may not prevent this.
      // From the Hydra docs:
      // Sets which "level" (e.g. 2-factor authentication) of authentication the user has. The value is really arbitrary
      // and optional. In the context of OpenID Connect, a value of 0 indicates the lowest authorization level.
      // acr: '0',
      // If that variable is not set, the ACR value will be set to the default passed here ('0')
      acr: '0',
      cancel: '',
    };
    if (!!this.cancel && request.subject !== this.cancel) {
      request.cancel = this.cancel;
    }

    try {
      const res = await HydraShell.AcceptOAuth2LoginRequest(authorizedFetch<AcceptLoginResponse>(weaveJWT), request);
      localStorageHelper.delete(loginChallenge);
      return res;
    } catch (err: any) {
      // if error contains 'login request was cancelled' then we need to redirect to the originating app in order to get a new login challenge
      // this is because the user previously logged in is different from the current user and hydra will need a different session token
      // this type of error has special handling elsewhere
      console.error(err);
      if (err.message?.includes('login request was cancelled')) {
        throw new Error('login request was cancelled');
      }
      throw err; // rethrow the error so the caller can handle it
    }
  }

  public async initializeConsentFlow(weaveJWT: string, challenge?: string): Promise<InitialConsentResponse> {
    this.challenge = challenge ?? this.getConsentChallenge();
    return await HydraShell.InitialOAuth2ConsentRequest(authorizedFetch<AcceptConsentResponse>(weaveJWT), {
      challenge: this.challenge,
    });
  }

  public async acceptConsentFlow(
    weaveJWT: string,
    scope: string[],
    clientID: string | undefined,
    challenge?: string
  ): Promise<AcceptLoginResponse> {
    this.challenge = challenge ?? this.getConsentChallenge();
    const res = await HydraShell.AcceptOAuth2ConsentRequest(authorizedFetch<AcceptLoginResponse>(weaveJWT), {
      challenge: this.challenge,
      scope: scope,
      clientId: clientID,
    });
    localStorageHelper.delete(consentChallenge);
    if (!!res.redirectTo) {
      navigateToUrl(res.redirectTo);
      return new Promise<never>(() => {});
    }
    return res;
  }

  public async initializeLogoutFlow(weaveJWT: string): Promise<InitialLogoutResponse> {
    return await HydraShell.InitialOAuth2LogoutRequest(authorizedFetch<InitialLogoutResponse>(weaveJWT), {
      challenge: this.getLogoutChallenge(),
    });
  }

  public async acceptLogoutFlow(weaveJWT: string): Promise<AcceptLogoutResponse> {
    //TODO: sign out of jackson?
    //https://boxyhq.com/docs/jackson/sso-flow#4-saml-slo
    const res = await HydraShell.AcceptOAuth2LogoutRequest(authorizedFetch<AcceptLogoutResponse>(weaveJWT), {
      challenge: this.getLogoutChallenge(),
    });
    localStorageHelper.delete(logoutChallenge);
    return res;
  }

  public getLoginChallenge(): string {
    // get challenge from query string param named 'login_challenge'
    const urlParams = new URLSearchParams(window.location.search);
    const challenge = urlParams.get(loginChallenge);
    if (!!challenge) {
      localStorageHelper.create(loginChallenge, challenge);
      return challenge;
    } else {
      const challengeFromLocalStorage = localStorageHelper.get(loginChallenge);
      if (!!challengeFromLocalStorage) {
        return challengeFromLocalStorage;
      }
    }
    return '';
  }

  private getConsentChallenge(): string {
    // get challenge from query string param named 'consent_challenge'
    const urlParams = new URLSearchParams(window.location.search);
    const challenge = urlParams.get(consentChallenge);
    if (!challenge) {
      return '';
    }
    localStorageHelper.create(consentChallenge, challenge);
    return challenge;
  }

  private getLogoutChallenge(): string {
    // get challenge from query string param named 'logout_challenge'
    const urlParams = new URLSearchParams(window.location.search);
    const challenge = urlParams.get(logoutChallenge);
    if (!challenge) {
      // if no challenge, check local storage (we are likely returning from the workforce logout)
      const challengeFromLocalStorage = localStorageHelper.get(logoutChallenge);
      if (!!challengeFromLocalStorage) {
        return challengeFromLocalStorage;
      }
      return '';
    }
    localStorageHelper.create(logoutChallenge, challenge);
    return challenge;
  }
}

const hydraShellService = new HydraShellService();
export { hydraShellService };

export type { HydraShellService };
